import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { useEditorStore } from "@/stores/editor/editor-store";

export class ButtonCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Variant patterns
    this.commandPatterns.set(
      /(?:make |set )?(?:button )?(?:variant |style )?(?:to )?(primary|secondary|outline|ghost|link|destructive)/i,
      (match) => ({ properties: { variant: match[1].toLowerCase() } })
    );

    // Size patterns
    this.commandPatterns.set(
      /(?:make |set )?(?:button )?(?:size )?(?:to )?(small|medium|large|sm|md|lg|xs|xl)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          small: "sm", sm: "sm", xs: "xs",
          medium: "md", md: "md",
          large: "lg", lg: "lg", xl: "xl"
        };
        const size = match[1].toLowerCase();
        return { properties: { size: sizeMap[size] || size } };
      }
    );

    // Text/Label patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:text|label|caption) (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { text: match[1] } })
    );

    // Icon patterns
    this.commandPatterns.set(
      /(?:add |set )?icon (?:to )?["']?([a-zA-Z0-9-_]+)["']?/i,
      (match) => ({ properties: { iconName: match[1], iconLibrary: 'lucide' } })
    );

    this.commandPatterns.set(
      /remove icon/i,
      () => ({ properties: { iconName: null, iconLibrary: null } })
    );

    // Icon position patterns
    this.commandPatterns.set(
      /icon (?:on |to )?(?:the )?(left|right)/i,
      (match) => ({ properties: { iconPosition: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /(?:move |put )?icon (?:on |to )?(?:the )?(left|right)/i,
      (match) => ({ properties: { iconPosition: match[1].toLowerCase() } })
    );

    // Icon size patterns
    this.commandPatterns.set(
      /(?:make )?icon (larger|bigger|smaller)/i,
      (match) => {
        const currentSize = 16; // default
        const adjustment = match[1].toLowerCase() === 'smaller' ? -4 : 4;
        return { properties: { iconSize: Math.max(12, Math.min(32, currentSize + adjustment)) } };
      }
    );

    this.commandPatterns.set(
      /icon size (\d+)/i,
      (match) => ({ properties: { iconSize: parseInt(match[1], 10) } })
    );

    // Disabled state
    this.commandPatterns.set(
      /(enable|disable)(?:d)?(?:\s+button)?/i,
      (match) => ({ properties: { disabled: match[1].toLowerCase() === "disable" } })
    );

    // Full width
    this.commandPatterns.set(
      /(?:make )?(?:button )?full[\s-]?width/i,
      () => ({ properties: { fullWidth: true } })
    );

    // Loading state
    this.commandPatterns.set(
      /(?:set |show |add )?loading(?:\s+state)?/i,
      () => ({ properties: { loading: true } })
    );

    this.commandPatterns.set(
      /(?:remove |hide |stop )?loading(?:\s+state)?/i,
      () => ({ properties: { loading: false } })
    );

    // Border radius
    this.commandPatterns.set(
      /(?:make |set )?(?:button )?(rounded|square|pill)/i,
      (match) => {
        const style = match[1].toLowerCase();
        const radiusMap: Record<string, string> = {
          rounded: "8px",
          square: "0px",
          pill: "9999px"
        };
        return { style: { borderRadius: radiusMap[style] } };
      }
    );

    // Hover background pattern
    this.commandPatterns.set(
      /(?:set |make )?hover (?:background|bg)(?: color)?(?: to)? (.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { hoverBackgroundColor: colorValue } } : {};
      }
    );

    // Hover text color pattern (accepts 'text' or 'color')
    this.commandPatterns.set(
      /(?:set |make )?hover (?:text|color)(?: to)? (.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { hoverColor: colorValue } } : {};
      }
    );

    // Action type patterns (submit, reset, button)
    this.commandPatterns.set(
      /(?:set |make )?(?:button )?type (?:to )?(submit|reset|button)/i,
      (match) => ({ properties: { type: match[1].toLowerCase() } })
    );

    // Navigation action patterns (support #"Page Name", #'Page Name', "Page Name", etc.)
    this.commandPatterns.set(
      /(?:set |make |add )?(?:button )?navigation (?:to )?(?:page )?#?["']?([^"']+)["']?/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1] // Page name - will be resolved to pageId later
        }
      })
    );

    this.commandPatterns.set(
      /navigate to (?:page )?#?["']?([^"']+)["']?/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /(?:link|connect) to (?:page )?#?["']?([^"']+)["']?/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /remove navigation/i,
      () => ({
        properties: {
          action: undefined,
          target: undefined,
          navigationLabel: undefined
        }
      })
    );

    // Form submit action patterns
    this.commandPatterns.set(
      /(?:make |set )?(?:button )?(?:to )?submit (?:the )?form ["']?([^"']+)["']?/i,
      (match) => ({
        properties: {
          action: 'form-submit',
          target: match[1], // Form name/ID - will be resolved
          text: 'Submit'
        }
      })
    );

    this.commandPatterns.set(
      /(?:set )?action (?:to )?form-submit/i,
      () => ({
        properties: {
          action: 'form-submit'
        }
      })
    );

    this.commandPatterns.set(
      /submit (?:to )?(?:the )?["']?([^"']+)["']? form/i,
      (match) => ({
        properties: {
          action: 'form-submit',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:this )?(?:a )?submit button/i,
      () => ({
        properties: {
          action: 'form-submit',
          text: 'Submit'
        }
      })
    );

    this.commandPatterns.set(
      /remove (?:form )?submit(?:tion)?/i,
      () => ({
        properties: {
          action: undefined,
          target: undefined
        }
      })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.BUTTON && this.hasButtonKeywords(command);
  }

  private hasButtonKeywords(command: string): boolean {
    const buttonKeywords = [
      "button", "btn", "click", "press", "tap",
      "primary", "secondary", "outline", "ghost", "link",
      "variant", "text", "label", "caption",
      "icon", "loading", "disabled", "enable",
      "size", "small", "medium", "large",
      "rounded", "square", "pill",
      "submit", "reset", "action",
      "navigate", "navigation", "goto", "link to", "connect",
      "left", "right", // for icon positioning
      "form-submit", "form submit", "submit form" // form submission
    ];
    const lowerCommand = command.toLowerCase();
    return buttonKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  /**
   * Resolve form name to form ID
   * Searches components for matching form container
   */
  private resolveFormTarget(formName: string): { formId: string | null; formName: string | null; error?: string } {
    const editorStore = useEditorStore.getState();
    const components = editorStore.components || [];

    // Find form containers
    const formContainers = components.filter(
      c => c.type === 'form-container'
    );

    if (formContainers.length === 0) {
      return { formId: null, formName: null, error: "No forms found on this page" };
    }

    // Try exact match on label
    const exactMatch = formContainers.find(
      f => (f.properties?.label as string)?.toLowerCase() === formName.toLowerCase()
    );
    if (exactMatch) {
      return { formId: exactMatch.id, formName: exactMatch.properties?.label as string };
    }

    // Try partial match on label
    const partialMatch = formContainers.find(
      f => (f.properties?.label as string)?.toLowerCase().includes(formName.toLowerCase())
    );
    if (partialMatch) {
      return { formId: partialMatch.id, formName: partialMatch.properties?.label as string };
    }

    // Try match on ID
    const idMatch = formContainers.find(
      f => f.id === formName || f.id.includes(formName)
    );
    if (idMatch) {
      return { formId: idMatch.id, formName: idMatch.properties?.label as string || 'Form' };
    }

    // If only one form exists, use it
    if (formContainers.length === 1) {
      return {
        formId: formContainers[0].id,
        formName: formContainers[0].properties?.label as string || 'Form'
      };
    }

    // Multiple forms, couldn't match
    const availableForms = formContainers
      .map(f => `"${f.properties?.label || f.id}"`)
      .join(', ');

    return {
      formId: null,
      formName: null,
      error: `Form "${formName}" not found. Available forms: ${availableForms}`
    };
  }

  /**
   * Resolve page name to page ID
   * Supports exact match and partial match
   */
  protected override resolvePageTarget(pageName: string): { pageId: string | null; pageName: string | null; error?: string } {
    return super.resolvePageTarget(pageName);
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors
      const colorValue = this.parseColorValue(part);
      if (colorValue) {
        if (part.toLowerCase().includes("background") || part.toLowerCase().includes("bg")) {
          changes.style = { ...changes.style, backgroundColor: colorValue };
        } else if (part.toLowerCase().includes("text") || part.toLowerCase().includes("color")) {
          changes.style = { ...changes.style, color: colorValue };
        }
      }

      // Parse size values - IMPORTANT: Use 'size' not 'style' for dimensions
      if (part.includes("width") && !part.includes("full")) {
        const widthValue = this.parseNumericValue(part);
        if (widthValue) {
          changes.size = { ...changes.size, width: widthValue };
        }
      }

      if (part.includes("height")) {
        const heightValue = this.parseNumericValue(part);
        if (heightValue) {
          changes.size = { ...changes.size, height: heightValue };
        }
      }

      // Parse padding
      if (part.includes("padding")) {
        const paddingValue = this.parseNumericValue(part);
        if (paddingValue) {
          changes.style = { ...changes.style, padding: paddingValue };
        }
      }
    }

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      // Resolve page name to ID for navigation actions
      if (changes.properties?.target && changes.properties?.action === 'navigate') {
        const pageTarget = changes.properties.target as string;
        const resolution = this.resolvePageTarget(pageTarget);

        if (resolution.error || !resolution.pageId) {
          return {
            success: false,
            error: resolution.error || `Could not resolve page "${pageTarget}"`,
            suggestions: await this.getSuggestions(component)
          };
        }

        // Update with resolved page ID and store display name
        changes.properties.target = resolution.pageId;
        changes.properties.navigationLabel = resolution.pageName;
      }

      // Resolve form name to ID for form-submit actions
      if (changes.properties?.action === 'form-submit') {
        const formTarget = changes.properties.target as string | undefined;

        if (formTarget) {
          const resolution = this.resolveFormTarget(formTarget);

          if (resolution.error || !resolution.formId) {
            return {
              success: false,
              error: resolution.error || `Could not resolve form "${formTarget}"`,
              suggestions: await this.getSuggestions(component)
            };
          }

          // Update with resolved form ID
          changes.properties.target = resolution.formId;
        } else {
          // No target specified, try to find parent form or only form on page
          const editorStore = useEditorStore.getState();
          const components = editorStore.components || [];
          const formContainers = components.filter(c => c.type === 'form-container');

          // Check if button is inside a form
          const parentForm = formContainers.find(f => {
            // Check if component is a direct child
            return components.some(c => c.id === component.id && c.parentId === f.id);
          });

          if (parentForm) {
            changes.properties.target = parentForm.id;
          } else if (formContainers.length === 1) {
            // Use the only form on the page
            changes.properties.target = formContainers[0].id;
          } else if (formContainers.length > 1) {
            const formNames = formContainers.map(f => f.properties?.label || f.id).join(', ');
            return {
              success: false,
              error: `Multiple forms found. Please specify which form to submit: ${formNames}`,
              suggestions: formContainers.map(f => `Submit form "${f.properties?.label || f.id}"`)
            };
          } else {
            return {
              success: false,
              error: 'No forms found on this page. Please create a form first.',
              suggestions: ['Create a form']
            };
          }
        }
      }

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the button command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    const pages = useEditorStore.getState().pages || [];
    const currentPageId = useEditorStore.getState().currentPageId;
    const otherPages = pages.filter(p => p.id !== currentPageId);

    const pageExamples = otherPages.length > 0
      ? [
          `Navigate to "${otherPages[0].name}"`,
          `Link to page "${otherPages[0].name}"`,
          'Remove navigation'
        ]
      : [];

    return [
      'Set text to "Submit"',
      'Make button primary',
      'Set variant to secondary',
      'Make outline button',
      'Add icon "Check"',
      'Add icon "ArrowRight" on right',
      'Icon on left',
      'Icon on right',
      'Make icon larger',
      'Make icon smaller',
      'Icon size 20',
      'Remove icon',
      'Make button large',
      'Set size to small',
      'Disable button',
      'Enable button',
      'Show loading state',
      'Make full width',
      'Set type to submit',
      'Make rounded button',
      'Make pill shaped',
      'Set background color to blue',
      'Change text color to white',
      'Set hover background to darkblue',
      'Make hover color white',
      'Add padding 16px',
      'Make button ghost style',
      'Set text to "Click me" and make primary',
      'Add icon "Save" and make primary',
      ...pageExamples
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const pages = useEditorStore.getState().pages || [];
    const currentPageId = useEditorStore.getState().currentPageId;

    // Navigation suggestions
    if (component.properties?.action === 'navigate') {
      suggestions.push('Remove navigation', 'Change target page');
      const targetPageId = component.properties.target as string;
      const targetPage = pages.find(p => p.id === targetPageId);
      if (!targetPage) {
        suggestions.push('Fix broken navigation');
      }
    } else {
      const otherPages = pages.filter(p => p.id !== currentPageId);
      if (otherPages.length > 0) {
        suggestions.push(
          `Navigate to "${otherPages[0].name}"`,
          'Set up page navigation'
        );
      }
    }

    // Text suggestions
    if (!component.properties?.text) {
      suggestions.push('Set text to "Click here"', 'Add button label');
    } else {
      suggestions.push('Change button text', 'Update label');
    }

    // Variant suggestions
    const currentVariant = component.properties?.variant || "default";
    if (currentVariant !== "primary") {
      suggestions.push("Make button primary");
    }
    if (currentVariant !== "secondary") {
      suggestions.push("Set variant to secondary");
    }
    if (currentVariant !== "outline") {
      suggestions.push("Make outline button");
    }

    // Size suggestions
    const currentSize = component.properties?.size || "md";
    if (currentSize !== "lg") {
      suggestions.push("Make button large");
    }
    if (currentSize !== "sm") {
      suggestions.push("Make button small");
    }

    // Icon suggestions
    if (!component.properties?.iconName) {
      suggestions.push('Add icon "Check"', 'Add icon "ArrowRight"');
    } else {
      suggestions.push("Remove icon", "Change icon");

      // Icon position suggestions
      const currentPosition = component.properties.iconPosition || 'left';
      if (currentPosition === 'left') {
        suggestions.push("Move icon to right");
      } else {
        suggestions.push("Move icon to left");
      }

      // Icon size suggestions
      suggestions.push("Make icon larger", "Make icon smaller");
    }

    // State suggestions
    if (component.properties?.disabled) {
      suggestions.push("Enable button");
    } else {
      suggestions.push("Disable button");
    }

    if (!component.properties?.loading) {
      suggestions.push("Show loading state");
    } else {
      suggestions.push("Remove loading state");
    }

    // Style suggestions
    if (!component.properties?.fullWidth) {
      suggestions.push("Make full width");
    }

    // Hover suggestions
    if (!component.properties?.hoverBackgroundColor) {
      suggestions.push("Set hover background");
    }
    if (!component.properties?.hoverColor) {
      suggestions.push("Set hover color");
    }

    suggestions.push("Make rounded corners", "Add shadow effect");

    return suggestions.slice(0, 12);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate variant
    const variant = typeof changes.properties?.variant === "string" ? changes.properties.variant : undefined;
    if (variant) {
      const validVariants = ["primary", "secondary", "outline", "ghost", "link", "destructive"];
      if (!validVariants.includes(variant)) {
        return { valid: false, error: `Invalid variant: ${variant}` };
      }
    }

    // Validate size
    const size = typeof changes.properties?.size === "string" ? changes.properties.size : undefined;
    if (size) {
      const validSizes = ["xs", "sm", "md", "lg", "xl"];
      if (!validSizes.includes(size)) {
        return { valid: false, error: `Invalid size: ${size}` };
      }
    }

    // Validate button type
    const buttonType = typeof changes.properties?.type === "string" ? changes.properties.type : undefined;
    if (buttonType) {
      const validTypes = ["button", "submit", "reset"];
      if (!validTypes.includes(buttonType)) {
        return { valid: false, error: `Invalid button type: ${buttonType}` };
      }
    }

    // Validate navigation action
    if (changes.properties?.action === 'navigate') {
      const targetPageId = changes.properties.target as string;
      if (!targetPageId) {
        return { valid: false, error: 'Navigation action requires a target page' };
      }

      // Check if target page exists (already validated in parseCommand, but double-check)
      const pages = useEditorStore.getState().pages || [];
      const targetPage = pages.find(p => p.id === targetPageId);

      if (!targetPage) {
        return { valid: false, error: `Target page not found: ${targetPageId}` };
      }

      // Check if navigation is allowed to target page
      if (targetPage.navigation?.allowNavigation === false) {
        return { valid: false, error: `Navigation to "${targetPage.name}" is not allowed` };
      }

      // Prevent navigation to current page
      const currentPageId = useEditorStore.getState().currentPageId;
      if (targetPageId === currentPageId) {
        return { valid: false, error: 'Cannot navigate to the current page' };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const buttonCommandHandler = new ButtonCommandHandler();
